import {
  AudioInputTest,
  AudioOutputTest,
  DiagnosticError,
  testAudioInputDevice,
  testAudioOutputDevice,
  WarningName,
} from '@twilio/rtc-diagnostics';
import { useState, useCallback } from 'react';
import { INPUT_TEST_DURATION, RECORD_DURATION } from '@/constants/diagnostics';
import { logger } from '@/services/logger';
import { setDiagnosticsAudioInputTestReport } from '@/store/features/diagnostics/slice';
import { useAppDispatch } from '@/store/hooks';
import { getAudioLevelPercentage, getStandardDeviation } from '@/utils/diagnostics';

let audioInputTest: AudioInputTest;
let audioOutputTest: AudioOutputTest;

const getErrorMessage = (error: DiagnosticError) => {
  let message = 'An unknown error has occurred';
  if (error) {
    message = error.domError ? error.domError.toString() : error.message;
  }
  return message;
};

export default function useAudioTest() {
  const dispatch = useAppDispatch();

  const [isRecording, setIsRecording] = useState(false);
  const [isAudioInputTestRunning, setIsAudioInputTestRunning] = useState(false);
  const [isAudioOutputTestRunning, setIsAudioOutputTestRunning] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [outputLevel, setOutputLevel] = useState(0);
  const [playbackURI, setPlaybackURI] = useState('');
  const [error, setError] = useState('');

  const playAudio = useCallback(
    (options: AudioOutputTest.Options) => {
      logger.event('AudioOutputTest running');

      options = { doLoop: false, ...options };
      audioOutputTest = testAudioOutputDevice(options);
      setIsAudioOutputTestRunning(true);

      audioOutputTest.on(AudioOutputTest.Events.Volume, (value: number) => {
        setOutputLevel(getAudioLevelPercentage(value));
      });

      audioOutputTest.on(AudioOutputTest.Events.End, (report: AudioOutputTest.Report) => {
        setIsAudioOutputTestRunning(false);
        setOutputLevel(0);

        const stdDev = getStandardDeviation(report.values);
        if (stdDev === 0) {
          setError('No audio detected');
        }
        logger.event('AudioOutputTest ended', report);
        dispatch(setDiagnosticsAudioInputTestReport(report));
      });

      audioOutputTest.on(AudioOutputTest.Events.Error, (diagnosticError: DiagnosticError) => {
        logger.error(diagnosticError);
        setError(getErrorMessage(diagnosticError));
      });
    },
    [dispatch]
  );

  const readAudioInput = useCallback(
    (options: AudioInputTest.Options) => {
      if (audioInputTest) {
        audioInputTest.stop();
      }

      logger.event('AudioInputTest running');
      const duration = options.enableRecording ? RECORD_DURATION : INPUT_TEST_DURATION;
      options = { duration, ...options };
      audioInputTest = testAudioInputDevice(options);

      setIsAudioInputTestRunning(true);
      if (options.enableRecording) {
        logger.event('Recording audio');
        setIsRecording(true);
      }

      audioInputTest.on(AudioInputTest.Events.Volume, (value: number) => {
        setInputLevel(getAudioLevelPercentage(value));
      });

      audioInputTest.on(AudioInputTest.Events.End, (report: AudioInputTest.Report) => {
        if (playbackURI && report.recordingUrl) {
          URL.revokeObjectURL(playbackURI);
        }

        if (report.recordingUrl) {
          setPlaybackURI(report.recordingUrl);
        }

        setIsRecording(false);
        setIsAudioInputTestRunning(false);
        logger.event('AudioInputTest ended', report);
        dispatch({ type: 'set-audio-input-test-report', report: report });
      });

      audioInputTest.on(AudioInputTest.Events.Error, (diagnosticError: DiagnosticError) => {
        logger.error(diagnosticError);
        setError(getErrorMessage(diagnosticError));
      });
      audioInputTest.on(AudioInputTest.Events.Warning, (name: WarningName) => {
        logger.event('warning', { name });
      });
      audioInputTest.on(AudioInputTest.Events.WarningCleared, (name: WarningName) => {
        logger.event('warning-cleared', { name });
      });
    },
    [playbackURI, dispatch]
  );

  const stopAudioTest = useCallback(() => {
    if (audioInputTest) {
      audioInputTest.stop();
      setInputLevel(0);
    }
    if (audioOutputTest) {
      audioOutputTest.stop();
      setOutputLevel(0);
    }
  }, []);

  return {
    error,
    setError,
    isRecording,
    isAudioInputTestRunning,
    isAudioOutputTestRunning,
    playAudio,
    playbackURI,
    readAudioInput,
    stopAudioTest,
    inputLevel,
    outputLevel,
  };
}
